import { useEffect, useRef, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { makeReqTracker } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Upload, Trash2, GraduationCap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CLASSES = Array.from({ length: 12 }, (_, i) => String(i + 1));
const SECTIONS = ["A", "B", "C", "D"];
const sectionsFor = (cls) => (cls === "11" || cls === "12" ? ["A", "B", "C", "D"] : ["A", "B", "C"]);

export default function StudentsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "editor";
  const [students, setStudents] = useState([]);
  const [filterClass, setFilterClass] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [q, setQ] = useState("");
  const [openSingle, setOpenSingle] = useState(false);
  const [openBulk, setOpenBulk] = useState(false);
  const trackerRef = useRef(makeReqTracker());

  const load = async () => {
    const reqId = trackerRef.current.next();
    const params = {};
    if (filterClass) params.class_name = filterClass;
    if (filterSection) params.section = filterSection;
    if (q) params.q = q;
    try {
      const { data } = await api.get("/students", { params });
      if (trackerRef.current.isLatest(reqId)) setStudents(data);
    } catch (e) {
      if (trackerRef.current.isLatest(reqId)) toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterClass, filterSection, q]);

  const remove = async (id) => {
    if (!window.confirm("Remove this student?")) return;
    try {
      await api.delete(`/students/${id}`);
      toast.success("Student removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell active="/students">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-carmel-gold mb-2">Roster</p>
          <h2 className="font-serif text-2xl md:text-4xl tracking-tight">Student Directory</h2>
          <p className="text-sm text-carmel-navy/60 mt-2">Classes I–XII · Sections A, B, C (plus Section D for XI &amp; XII)</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Dialog open={openBulk} onOpenChange={setOpenBulk}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="bulk-add-open"><Upload className="w-4 h-4 mr-2" /> Bulk Add</Button>
              </DialogTrigger>
              <BulkDialog onDone={() => { setOpenBulk(false); load(); }} />
            </Dialog>
            <Dialog open={openSingle} onOpenChange={setOpenSingle}>
              <DialogTrigger asChild>
                <Button className="bg-carmel-gold hover:bg-carmel-goldDark" data-testid="add-student-open"><Plus className="w-4 h-4 mr-2" /> Add Student</Button>
              </DialogTrigger>
              <SingleDialog onDone={() => { setOpenSingle(false); load(); }} />
            </Dialog>
          </div>
        )}
      </div>

      <div className="bg-white border border-carmel-navy/10 rounded-lg p-4 mb-6 shadow-card grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">Class</Label>
          <Select value={filterClass || "all"} onValueChange={(v) => setFilterClass(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-class-select" className="mt-1"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">Section</Label>
          <Select value={filterSection || "all"} onValueChange={(v) => setFilterSection(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-section-select" className="mt-1"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sections</SelectItem>
              {SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">Search by name</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type name..." data-testid="filter-search-input" className="mt-1" />
        </div>
      </div>

      <div className="bg-white border border-carmel-navy/10 rounded-lg overflow-x-auto shadow-card">
        <Table>
          <TableHeader className="bg-carmel-slateBg">
            <TableRow>
              <TableHead className="uppercase text-xs tracking-wider">Name</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Class</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Section</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Roll</TableHead>
              {canEdit && <TableHead className="uppercase text-xs tracking-wider text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.length === 0 ? (
              <TableRow><TableCell colSpan={canEdit ? 5 : 4} className="text-center py-12 text-carmel-navy/50">
                <GraduationCap className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No students yet. {canEdit && "Use \"Bulk Add\" to import a class."}
              </TableCell></TableRow>
            ) : students.map((s) => (
              <TableRow key={s.id} className="hover:bg-carmel-slateBg" data-testid={`student-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>Class {s.class_name}</TableCell>
                <TableCell>{s.section}</TableCell>
                <TableCell className="text-carmel-navy/60">{s.roll_number || "—"}</TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`student-delete-${s.id}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </AppShell>
  );
}

function SingleDialog({ onDone }) {
  const [name, setName] = useState("");
  const [roll, setRoll] = useState("");
  const [cls, setCls] = useState("1");
  const [sec, setSec] = useState("A");
  const [busy, setBusy] = useState(false);

  const availableSecs = sectionsFor(cls);
  useEffect(() => { if (!availableSecs.includes(sec)) setSec(availableSecs[0]); }, [cls, availableSecs, sec]);

  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      await api.post("/students", { name: name.trim(), roll_number: roll || null, class_name: cls, section: sec });
      toast.success("Student added");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <DialogContent>
      <DialogHeader><DialogTitle className="font-serif text-2xl">Add Student</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Full Name</Label>
          <Input data-testid="student-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Class</Label>
            <Select value={cls} onValueChange={setCls}>
              <SelectTrigger data-testid="student-class-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Section</Label>
            <Select value={sec} onValueChange={setSec}>
              <SelectTrigger data-testid="student-section-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{availableSecs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Roll (opt.)</Label>
            <Input data-testid="student-roll-input" value={roll} onChange={(e) => setRoll(e.target.value)} className="mt-1" />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button className="bg-carmel-gold hover:bg-carmel-goldDark" onClick={save} disabled={busy} data-testid="student-save-button">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function BulkDialog({ onDone }) {
  const [cls, setCls] = useState("1");
  const [sec, setSec] = useState("A");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const availableSecs = sectionsFor(cls);
  useEffect(() => { if (!availableSecs.includes(sec)) setSec(availableSecs[0]); }, [cls, availableSecs, sec]);

  const save = async () => {
    const names = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (names.length === 0) return toast.error("Paste at least one name");
    setBusy(true);
    try {
      const { data } = await api.post("/students/bulk", { class_name: cls, section: sec, names });
      toast.success(`Added ${data.inserted} students to ${cls}${sec}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle className="font-serif text-2xl">Bulk Add Students</DialogTitle></DialogHeader>
      <p className="text-sm text-carmel-navy/60">Paste one student name per line. Choose target class & section (Classes 11 and 12 also have section D).</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Class</Label>
          <Select value={cls} onValueChange={setCls}>
            <SelectTrigger data-testid="bulk-class-select" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Section</Label>
          <Select value={sec} onValueChange={setSec}>
            <SelectTrigger data-testid="bulk-section-select" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{availableSecs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Textarea
        rows={10}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"Rahul Sharma\nPriya Menon\nAaron D'Souza"}
        data-testid="bulk-names-textarea"
      />
      <DialogFooter>
        <Button className="bg-carmel-gold hover:bg-carmel-goldDark" onClick={save} disabled={busy} data-testid="bulk-save-button">Add Students</Button>
      </DialogFooter>
    </DialogContent>
  );
}
